import { useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { env } from '@/constants/env';
import { uploadImage as uploadImageToEstateMedia } from '@/features/estate-media/api/upload-image';
import { getImage } from '@/features/image-rendering/api/image';
import { uploadImage as uploadImageToProjectMedia } from '@/features/project-media/api/upload-image';
import type { RenderingImage } from '@/features/renderings/shared/atoms/image-files';
import {
  renderingsSelectedPreviewImageAtom,
  renderingsUploadedImagesAtom,
} from '@/features/renderings/shared/atoms/image-files';
import { createTempId } from '@/utils/tempId';

type CreateMagicEditPayload = {
  image: RenderingImage;
  mask: Blob | null;
  mode: 'free-edit' | 'remove';
  prompt: string;
  estateId?: string;
  projectId?: string;
};

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const getImageFileName = (file: File) => {
  if (/\.(png|jpe?g|webp)$/i.test(file.name)) return file.name;
  if (file.type === 'image/jpeg') return `${file.name}.jpg`;
  if (file.type === 'image/webp') return `${file.name}.webp`;
  return `${file.name}.png`;
};

const getSourceImageFile = async (image: RenderingImage) => {
  if (image.file) return image.file;

  const response = image.originalImageId
    ? await getImage(image.originalImageId)
    : await fetch(image.originalImage);
  const blob = await response.blob();

  return new File([blob], 'image', { type: blob.type || 'image/jpeg' });
};

export const useMagicEditCreate = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const setSelectedPreviewImage = useSetAtom(renderingsSelectedPreviewImageAtom);
  const setUploadedImages = useSetAtom(renderingsUploadedImagesAtom);

  const createMagicEdit = useCallback(
    async (payload: CreateMagicEditPayload) => {
      setIsLoading(true);
      try {
        const image = await getSourceImageFile(payload.image);
        if (!image) throw new Error('Failed to get image');

        const formData = new FormData();
        formData.append('image', image, getImageFileName(image));
        if (payload.mask) {
          formData.append('mask', payload.mask, 'mask.png');
        }
        formData.append('prompt', payload.prompt);

        const endpoint = payload.mode === 'remove' ? 'remove' : 'free-edit';
        formData.append('mode', endpoint);
        const response = await fetch(`${env.API_URL}/magic-edit`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Failed to start magic edit');
        }

        const contentType = response.headers.get('content-type') ?? '';
        let editedBlob: Blob;

        if (contentType.startsWith('image/')) {
          editedBlob = await response.blob();
        } else {
          const data = (await response.json()) as { task_id?: string };
          if (!data.task_id) throw new Error('Magic edit task id missing');

          toast.info(t('magic-edit-successfully-started'));
          editedBlob = await pollMagicEditResult(data.task_id);
        }

        const editedImage = new File([editedBlob], 'magic-edit.png', {
          type: editedBlob.type || 'image/png',
        });
        const editedImageUrl = URL.createObjectURL(editedImage);
        let renderedImageId: string | undefined;

        try {
          if (payload.estateId) {
            const uploadedImage = await uploadImageToEstateMedia({
              image: editedImage,
              estateId: payload.estateId,
            });
            renderedImageId = uploadedImage[0]?.mediaId ?? undefined;
          } else if (payload.projectId) {
            const uploadedImage = await uploadImageToProjectMedia({
              image: editedImage,
              projectId: payload.projectId,
            });
            renderedImageId = uploadedImage[0]?.mediaId ?? undefined;
          }
        } catch {
          toast.error(t('magic-edit-media-upload-failed'));
        }

        const generatedImage: RenderingImage = {
          ...payload.image,
          id: createTempId(),
          file: editedImage,
          renderedImage: editedImageUrl,
          renderedImageId,
          magicEditMode: payload.mode,
          renderingType: undefined,
        };

        setUploadedImages((prev) => [...prev, generatedImage]);
        setSelectedPreviewImage(generatedImage);

        toast.success(t('magic-edit-success'));
      } finally {
        setIsLoading(false);
      }
    },
    [setSelectedPreviewImage, setUploadedImages, t]
  );

  return { createMagicEdit, isLoading };
};

const pollMagicEditResult = async (taskId: string) => {
  for (let attempt = 0; attempt < 60; attempt += 1) {
    await wait(10_000);

    const response = await fetch(`${env.API_URL}/magic-edit/result/${taskId}`, {
      credentials: 'include',
    });

    if (!response.ok && response.status !== 202) {
      throw new Error('Failed to get magic edit result');
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.startsWith('image/')) {
      return await response.blob();
    }
  }

  throw new Error('Magic edit timed out');
};
